/**
 * MainLayout component serves as the primary layout for the application.
 * It handles routing, socket initialization, notifications, and various UI components.
 * 
 * @class MainLayout
 * @extends {Component}
 * 
 * @property {Object} state - The component's state.
 * @property {boolean} state.trackOrderVisible - Determines if the track order popup is visible.
 * @property {string} state.direction - Tracks the scroll direction.
 * @property {number} state.lastScrollPos - Stores the last scroll position.
 * @property {Object|null} state.socket - The socket.io client instance.
 * @property {number} state.vh - The viewport height for handling lock scroll.
 * 
 * @property {Object} props - The component's props.
 * @property {boolean} props.loading - Indicates if the application is in a loading state.
 * @property {boolean} props.sideMenuOpen - Indicates if the side menu is open.
 * @property {boolean} props.lockScroll - Indicates if scrolling is locked.
 * @property {boolean} props.authenticated - Indicates if the user is authenticated.
 * @property {Array} props.paths - List of static paths for routing.
 * @property {Array} props.categories - List of item categories.
 * @property {string} props.status - The current status of the application (e.g., online/offline).
 * @property {Object} props.location - The current location object from react-router.
 * @property {Function} props.history - The history object from react-router for navigation.
 * @property {Function} props.clearOrder - Action to clear the order state.
 * @property {Function} props.showAuthenticationPopup - Action to show the authentication popup.
 * @property {Function} props.signinWithCookiePromise - Action to sign in using cookies.
 * @property {Function} props.queryStatus - Action to query the application's status.
 * @property {Function} props.swal - Action to show a SweetAlert popup.
 * @property {Function} props.close - Action to close a SweetAlert popup.
 * @property {Function} props.setMaintenance - Action to set the maintenance status.
 * @property {Function} props.postSocketError - Action to handle socket errors.
 * @property {Function} props.reportClientError - Action to report a caught client-side error to the durable sink.
 * @property {Function} props.getAllCategories - Action to fetch all item categories.
 * @property {Function} props.getUserNotification - Action to fetch user notifications.
 * @property {Function} props.showRoyalPopup - Action to show the royal popup.
 * @property {Function} props.setEID - Action to set the EID from the URL.
 * @property {Function} props.getStaticPaths - Action to fetch static paths for routing.
 * @property {Function} props.getPageLayoutStrapi - Action to fetch page layout data from Strapi.
 * @property {Function} props.accessTokenVerify - Action to verify the access token.
 * 
 * @method componentDidUpdate - Lifecycle method to handle updates to props and state.
 * @method componentDidMount - Lifecycle method to handle initialization logic.
 * @method componentDidCatch - Lifecycle method to handle errors in the component tree.
 * @method accessTokenVerifyCall - Verifies the access token when the user changes.
 * @method setupBeforeUnloadListener - Sets up a listener for the beforeunload event.
 * @method doBeforeUnload - Handles cleanup before the window unloads.
 * @method initSocket - Initializes the socket.io client and sets up event listeners.
 * @method getAnnouncement - Fetches announcements from the Strapi API.
 * @method autoShowRoyalPopup - Automatically shows the royal popup based on conditions.
 * @method showTrackOrder - Toggles the visibility of the track order popup.
 * 
 * @returns {JSX.Element} The rendered MainLayout component.
 */
import 'sweetalert/dist/sweetalert.css';
import React, { Component } from 'react';
import { get, isEqual, map } from 'lodash';
import moment from 'moment';
import { frontloadConnect } from 'react-frontload';
import Loadable from 'react-loadable';
import NotificationSystem from 'react-notification-system';
import { connect } from 'react-redux';
import ReduxSweetAlert, { swal, close } from 'react-redux-sweetalert';
import { Route, Switch, withRouter, Redirect } from 'react-router-dom'; //Redirect
import io from 'socket.io-client';
import { signinWithCookiePromise, accessTokenVerify, getRedirectFromProvider, showPDPAPopup } from './actions/auth/index';
import { postSocketError } from './actions/chat/index';
import { reportClientError } from './actions/error/index';
import { showAuthenticationPopup, queryStatus, setMaintenance, showRoyalPopup, setEID } from './actions/common/index';
import { getCouponFromUrl, getCoupons } from './actions/coupon/index'; //displayCouponPopup
import { getAllCategories, getAllCategoriesPromise } from './actions/item-category/index';
import { getStaticPaths, getPageLayoutStrapi } from './actions/layout/index'
import { getUserNotification } from './actions/notification/index'
import { clearOrder } from './actions/order/index';
import { getDeliveryRates } from './actions/delivery/index';
import { getJsonFromUrl } from './assets/util';
import { getStrapiAPI } from './assets/strapi';
import { loadFirebaseAuth } from './assets/firebase';
import { checkAndReloadIfNeeded } from './utils/buildVersionChecker';
import AuthenticationPopup from './components/AuthenticationPopup';
// Item 01b. Both are mounted at the layout level rather than inside the signup
// forms, because `AuthenticationPopup` unmounts the instant `authenticated`
// flips true — a panel rendered inside it would flash for one frame. Both
// render nothing at all unless the backend profile says the address is
// unverified, so they cost an anonymous visitor nothing.
import EmailVerificationBanner from './components/auth/verification/EmailVerificationBanner';
import EmailVerificationPanel from './components/auth/verification/EmailVerificationPanel';
import CookieConsent from './components/common/CookieConsent';
import ScrollToTop from './components/common/ScrollToTop';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ResponsiveMenu from './components/common/ResponsiveMenu';
import RoyalPopup from './components/common/RoyalPopup';
import Loading from './components/Loading';
import PDPAConsentPopup from './components/PDPAConsentPopup';
import { IP, STATUS } from './config';
import Home from './views/Home';
import ItemAddedToCartNotify from './views/ItemAddedToCartNotify';
import PageRender from './views/PageRender';
import Maintenance from './views/Maintenance';

const AppOnlyPage = Loadable({
    loader: () => import(/* webpackChunkName: "AppOnlyPage"*/
        './views/AppOnly'),
    loading: () => null,
    modules: ['AppOnlyPage']
});

const ChatAndShopOrder = Loadable({
    loader: () => import(/* webpackChunkName: "ChatAndShopOrder"*/
        './views/ChatAndShopOrder'),
    loading: () => null,
    modules: ['ChatAndShopOrder']
});
const ChatAndShopThankyou = Loadable({
    loader: () => import(/* webpackChunkName: "ChatAndShopThankyou"*/
        './views/ChatAndShopThankyou'),
    loading: () => null,
    modules: ['ChatAndShopThankyou']
});
const LiveChat = Loadable({
    loader: () => import(/* webpackChunkName: "LiveChat"*/
        './components/livechat/LiveChat'),
    loading: () => null,
    modules: ['LiveChat']
});
const Page404 = Loadable({
    loader: () => import(/* webpackChunkName: "404"*/
        './views/error/NotFound'),
    loading: () => null,
    modules: ['NotFound']
});
const About = Loadable({
    loader: () => import(/* webpackChunkName: "About"*/
        './views/About'),
    loading: () => null,
    modules: ['About']
});
const AccountLayout = Loadable({
    loader: () => import(/* webpackChunkName: "AccountLayout"*/
        './components/layout/Account'),
    loading: () => null,
    modules: ['Account']
});

const BlogGrid = Loadable({
    loader: () => import(/* webpackChunkName: "BlogGrid"*/
        './views/BlogGrid'),
    loading: () => null,
    modules: ['BlogGrid']
});
const BlogContent = Loadable({
    loader: () => import(/* webpackChunkName: "BlogContent" */
        './views/blog/BlogContent'),
    loading: () => null,
    modules: ['BlogContent']
});
const BrandList = Loadable({
    loader: () => import(/* webpackChunkName: "BrandList"*/
        './views/BrandList'),
    loading: () => null,
    modules: ['BrandList']
});
const Checkout = Loadable({
    loader: () => import(/* webpackChunkName: "Checkout"*/
        './views/Checkout'),
    loading: () => null,
    modules: ['Checkout']
});
const CheckoutThankYou = Loadable({
    loader: () => import(/* webpackChunkName: "CheckoutThankYou"*/
        './views/CheckoutThankYou'),
    loading: () => null,
    modules: ['CheckoutThankYou']
});
const ContactUs = Loadable({
    loader: () => import(/* webpackChunkName: "ContactUs"*/
        './views/ContactUs'),
    loading: () => null,
    modules: ['ContactUs']
});
const Deal = Loadable({
    loader: () => import(/* webpackChunkName: "Deal"*/
        './views/Deal'),
    loading: () => null,
    modules: ['Deal']
});
const DealCreditCard = Loadable({
    loader: () => import(/* webpackChunkName: "DealCreditCard"*/
        './views/DealCreditCard'),
    loading: () => null,
    modules: ['DealCreditCard']
});
const Flashsale = Loadable({
    loader: () => import(/* webpackChunkName: "Flashsale"*/
        './views/Flashsale'),
    loading: () => null,
    modules: ['Flashsale']
});
const ForgotView = Loadable({
    loader: () => import(/* webpackChunkName: "ForgotView"*/
        './views/Forgot'),
    loading: () => null,
    modules: ['ForgotView']
});
const Howto = Loadable({
    loader: () => import(/* webpackChunkName: "Howto"*/
        './views/Howto'),
    loading: () => null,
    modules: ['Howto']
});
// const KoreaMain = Loadable({
//     loader: () => import(/* webpackChunkName: "KoreaMain"*/
//         './views/KoreaMain'),
//     loading: () => null,
//     modules: ['KoreaMain']
// });
const LoginView = Loadable({
    loader: () => import(/* webpackChunkName: "LoginView"*/
        './views/Login'),
    loading: () => null,
    modules: ['LoginView']
});
const OrderDetailView = Loadable({
    loader: () => import(/* webpackChunkName: "OrderDetailView"*/
        './components/account/order/OrderDetail'),
    loading: () => null,
    modules: ['OrderDetailView']
});
const PrivacyPolicy = Loadable({
    loader: () => import(/* webpackChunkName: "PrivacyPolicy" */
        './views/PrivacyPolicy'),
    loading: () => null,
    modules: ['PrivacyPolicy']
});
const CookiePolicy = Loadable({
    loader: () => import(/* webpackChunkName: "CookiePolicy" */
        './views/CookiePolicy'),
    loading: () => null,
    modules: ['CookiePolicy']
});
const ProductDetail = Loadable({
    loader: () => import(/* webpackChunkName: "ProductDetail" */
        './views/ProductDetail'),
    loading: () => null,
    modules: ['ProductDetail']
});
const Partner = Loadable({
    loader: () => import(/* webpackChunkName: "Partner"*/
        './views/Partner'),
    loading: () => null,
    modules: ['Partner']
})
const StoreGrid = Loadable({
    loader: () => import(/* webpackChunkName: "StoreGrid"*/
        './views/StoreGrid'),
    loading: () => null,
    modules: ['StoreGrid']
});
const StoreLocation = Loadable({
    loader: () => import(/* webpackChunkName: "StoreLocation"*/
        './views/StoreLocation'),
    loading: () => null,
    modules: ['StoreLocation']
});
const SearchResult = Loadable({
    loader: () => import(/* webpackChunkName: "SearchResult"*/
        './views/SearchResult'),
    loading: () => null,
    modules: ['SearchResult']
});
const TermOfService = Loadable({
    loader: () => import(/* webpackChunkName: "TermOfService" */
        './views/TermOfService'),
    loading: () => null,
    modules: ['TermOfService']
});
const ExchangeReturn = Loadable({
    loader: () => import(/* webpackChunkName: "ExchangeReturn" */
        './views/ExchangeReturn'),
    loading: () => null,
    modules: ['ExchangeReturn']
});
const TrackOrderPopup = Loadable({
    loader: () => import(/* webpackChunkName: "TrackOrderPopup"*/
        './components/TrackOrderPopup'),
    loading: () => null,
    modules: ['TrackOrderPopup']
});
const UploadReceipt = Loadable({
    loader: () => import(/* webpackChunkName: "UploadReceipt"*/
        './views/UploadReceipt'),
    loading: () => null,
    modules: ['UploadReceipt']
});
const UploadReceiptThankYou = Loadable({
    loader: () => import(/* webpackChunkName: "UploadReceiptThankYou"*/
        './views/UploadReceiptThankYou'),
    loading: () => null,
    modules: ['UploadReceiptThankYou']
});
const AddReview = Loadable({
    loader: () => import(/* webpackChunkName: "AddReview"*/
        './views/rich_comment/AddReview'),
    loading: () => null,
    modules: ['AddReview']
});
const SelectShade = Loadable({
    loader: () => import(/* webpackChunkName: "SelectShade"*/
        './views/rich_comment/SelectShade'),
    loading: () => null,
    modules: ['SelectShade']
});
const GiveawayMain = Loadable({
    loader: () => import(/* webpackChunkName: "GiveawayMain"*/
        './views/giveaways/GiveawayMain'),
    loading: () => null,
    modules: ['GiveawayMain']
})

const GiveawayForm = Loadable({
    loader: () => import(/* webpackChunkName: "GiveawayForm"*/
        './views/giveaways/GiveawayForm'),
    loading: () => null,
    modules: ['GiveawayForm']
})

const GiveawayDetail = Loadable({
    loader: () => import(/* webpackChunkName: "GiveawayDetail"*/
        './views/giveaways/GiveawayDetail'),
    loading: () => null,
    modules: ['GiveawayDetail']
})

const TopSpender = Loadable({
    loader: () => import(/* webpackChunkName: "TopSpender"*/
        './views/TopSpender'),
    loading: () => null,
    modules: ['TopSpender']
})

const FirebaseAction = Loadable({
    loader: () => import(/* webpackChunkName: "FirebaseAction"*/
        './views/FirebaseAction'),
    loading: () => null,
    modules: ['FirebaseAction']
})

/* eslint-enable */

const frontLoad = async props => {
    try {
        // Execute all data fetching in parallel
        const [, signinResult,] = await Promise.all([
            props.queryStatus(),
            props.signinWithCookiePromise(),
            props.getStaticPaths(),
        ]);

        // Load page layout in background (non-blocking)
        // if (paths) {
        //     const i = findIndex(paths, (p) => p.link === props.location.pathname);
        //     if (i !== -1) {
        //         // Don't await this - let it load in background
        //         props.getPageLayoutStrapi(paths[i].name).catch(error => {
        //             console.warn('Background layout loading failed:', error);
        //         });
        //     }
        // }

        return signinResult;
    } catch (error) {
        console.error('Error in frontLoad:', error);
        throw error;
    }
};

var unsubscribe;
// Guards against a second `attachIdTokenListener` starting while the first is
// still downloading the Firebase chunk — `componentDidUpdate` can fire again
// during that window and would otherwise attach two listeners.
var idTokenListenerPending = false;
class MainLayout extends Component {

    constructor(props) {
        super(props);

        this.state = {
            trackOrderVisible: false,
            direction: '',
            lastScrollPos: 0,
            socket: null,
            vh: 0,
        }

        // this.trackPixelEvent = this.trackPixelEvent.bind(this)
        this.autoShowRoyalPopup = this.autoShowRoyalPopup.bind(this)
        this.showTrackOrder = this.showTrackOrder.bind(this)
        this.initSocket = this.initSocket.bind(this)
        this.getAnnouncement = this.getAnnouncement.bind(this);
        this.accessTokenVerifyCall = this.accessTokenVerifyCall.bind(this);
        this.attachIdTokenListener = this.attachIdTokenListener.bind(this);
    }

    componentDidUpdate(prevProps) {
        // if (this.props.secret !== prevProps.secret && this.props.secret) {
        //     this.initSocket()
        // }

        if (this.props.notification !== prevProps.notification && prevProps.notification !== null) {
            this.refs.notificationSystem.addNotification(this.props.notification);
        }

        if (this.props.authenticated !== prevProps.authenticated && prevProps.authenticated === true) {
            if (this.socket) {
                this.socket.emit('user connect', this.props.secret, this.props.authenticated ? this.props.user_id : 1);
            }
        }

        if (!isEqual(this.props.location, prevProps.location)) {
            // Check for build version mismatch on route changes
            // This ensures users get the latest version when navigating
            if (process.env.NODE_ENV === 'production') {
                checkAndReloadIfNeeded();
            }

            if (this.socket) {
                this.socket.emit('set last page', this.props.secret, this.props.user_id, this.props.location.pathname)
            }
        }

        if (this.props.couponUrl !== prevProps.couponUrl) {
            this.props.history.push({ pathname: this.props.location.pathname })
        }

        if (this.props.message !== prevProps.message && prevProps.status === STATUS.ONLINE) {
            if (this.props.message) {
                this.props.swal({ type: this.props.type, title: this.props.title, text: this.props.message, html: true, onConfirm: this.props.close, customClass: 'bt-dialog-box' });
            }
        }

        if (!prevProps.lockScroll && this.props.lockScroll) {
            let vh = window.innerHeight * 0.01 * 100;
            this.setState({ vh });
        }
        if (prevProps.profile !== this.props.profile) {
            if (this.props.profile) {
                // Deliberately not awaited: this is a background token-refresh
                // listener, not something the render depends on. It handles and
                // reports its own failures, so no rejection escapes.
                this.attachIdTokenListener();
            } else if (unsubscribe) {
                unsubscribe();
                // Previously left set, which meant a sign-out followed by a
                // sign-in in the same session never re-attached the listener.
                unsubscribe = undefined;
            }
        }
    }

    componentDidMount() {
        // Check for build version mismatch and reload if necessary
        // This ensures users get the latest version when available
        if (process.env.NODE_ENV === 'production') {
            checkAndReloadIfNeeded();
        }

        // this.props.registerDevice();
        this.autoShowRoyalPopup()
        this.getAnnouncement();
        this.initSocket();
        this.props.getAllCategories();
        this.props.getDeliveryRates();
        const searchQuery = getJsonFromUrl(this.props.location.search);
        if (searchQuery.eid) {
            this.props.setEID(searchQuery.eid);
        }
    }

    /**
     * React error boundary hook for the whole application tree.
     *
     * This used to be a bare `console.log` with its recovery path commented out,
     * which meant a render error that unmounts the checkout funnel was invisible
     * to us — root `CLAUDE.md` standard 5 ("console as the only handling").
     * `reportClientError` keeps it in the console *and* persists it to
     * `POST /socket_error` -> `Chat_Debug`, the only durable browser-reachable
     * sink this estate has. See `src/actions/error/index.js` for why that sink
     * and what it does not give us (nothing pages anyone; that is a follow-up).
     *
     * The returned promise is deliberately not awaited — `componentDidCatch` is
     * synchronous — and `reportClientError` never rejects, so no promise is left
     * unterminated (root standard 1).
     *
     * No recovery navigation is performed. Restoring the commented-out
     * `history.push('/404')` is a user-visible behaviour change and is out of
     * scope for item 21c; React continues to unmount the subtree as it does today.
     *
     * @param {Error} error - The error thrown during render.
     * @param {{componentStack: string}} info - React's component stack (component names only).
     * @returns {void}
     */
    componentDidCatch(error, info) {
        this.props.reportClientError({
            error,
            componentStack: get(info, 'componentStack', null),
            context: 'MainLayout.componentDidCatch',
        });
    }

    /**
     * Attach Firebase's `onIdTokenChanged` refresh listener, loading the
     * Firebase chunk on demand.
     *
     * Only ever called once a profile exists, so an anonymous visitor never
     * downloads Firebase — that is the whole point of `assets/firebase.js`
     * being lazy. Never rejects.
     *
     * @returns {Promise<void>}
     */
    async attachIdTokenListener() {
        if (unsubscribe || idTokenListenerPending) {
            return;
        }
        idTokenListenerPending = true;
        try {
            const { auth, authApi } = await loadFirebaseAuth();
            // The user may have signed out while the chunk was downloading;
            // do not attach a listener for a profile that is gone.
            if (!this.props.profile) {
                return;
            }
            unsubscribe = authApi.onIdTokenChanged(auth, this.accessTokenVerifyCall);
        } catch (error) {
            // No dialog here on purpose: the visitor is already signed in and
            // browsing, and this listener is invisible to them. Losing it only
            // means the access token stops being refreshed silently, so the
            // next API call 401s and the existing 401 handling takes over.
            // Report it rather than swallow it, so the degradation is visible
            // in the error sink instead of nowhere.
            this.props.reportClientError({ error, context: 'MainLayout.attachIdTokenListener' });
        } finally {
            idTokenListenerPending = false;
        }
    }

    async accessTokenVerifyCall(user) {
        if (user) {
            const token = await user.getIdToken();
            this.props.accessTokenVerify(token);
        }
    }

    setupBeforeUnloadListener() {
        window.addEventListener("beforeunload", this.doBeforeUnload);
    };

    doBeforeUnload(e) {
        e.preventDefault()
        if (this.socket) {
            this.socket.emit('disconnect user', this.props.secret)
            this.socket.disconnect()
        }
    }

    initSocket() {
        // Transport was pinned to long-polling only. Git history is squashed to a
        // single "init" commit and there is no ADR, so the original reason is
        // unrecoverable; the likely cause is a historical proxy that did not pass
        // `Upgrade`. It is safe to lift now, verified three independent ways:
        //   1. api.thebeautrium.com answers the websocket upgrade with
        //      `101 Switching Protocols` (nginx has a dedicated `/socket.io/`
        //      location doing `proxy_http_version 1.1` + `Upgrade`/`Connection`);
        //   2. the server advertises both transports — `bt-backend/socket_handle.js:11`
        //      is `transports: ['websocket', 'polling']`;
        //   3. the shipped Flutter app already talks websocket to the same endpoint
        //      (`beautrium_mobile/lib/src/features/global/data/socket_repository.dart`).
        // Polling is kept as the second entry, and `tryAllTransports` makes the
        // client fall back to it when a restrictive proxy strips `Upgrade` instead
        // of giving up after the first transport fails. `tryAllTransports` needs
        // socket.io-client >= 4.7.5 and is *silently ignored* below that — hence
        // the `^4.7.5` floor in package.json. See
        // `work/21c-frontend-critical-path/design.md` change 3.
        this.socket = io(IP, { transports: ['websocket', 'polling'], tryAllTransports: true });
        this.socket.on('connect', () => {
            this.socket.emit('user connect', this.props.secret, this.props.authenticated ? this.props.user_id : 1);
        })
        this.socket.on('maintenance', (v) => {
            this.props.setMaintenance(v);
            if (v === STATUS.ONLINE) {
                window.location.reload()
            }
        })
        this.socket.on('error', (error) => {
            this.props.postSocketError(error)
        });

        this.socket.on('notification', (error) => {
            this.props.getUserNotification();
        })

        // `io(...)` auto-connects, so the `this.socket.connect()` that used to sit
        // here was a no-op on an already-connecting socket. Removed as dead code.
        this.setState({ socket: this.socket });
    }

    getAnnouncement() {
        getStrapiAPI(`/header-announcements`)
            .then((result) => {
                this.setState({ announcement: get(result, "announcement", null) })
            })
    }

    autoShowRoyalPopup() {
        const now = moment();
        getStrapiAPI(`/popups`)
            .then((result) => {
                let userForceCloseDate = localStorage.getItem('RoyalPopupForceCloseDate') ? moment(localStorage.getItem('RoyalPopupForceCloseDate')) : moment('1970-01-01')
                const show = { img: get(result, "image.url", null), name: get(result, "image.alternativeText", ""), link: get(result, "link", null), close_button_color: get(result, "close_button_color", "#ccc") }
                if (now.diff(userForceCloseDate, "m") >= 15) {
                    this.props.showRoyalPopup(show)
                }
            })

    }

    showTrackOrder(v = true) {
        this.props.clearOrder();
        this.setState((prev) => { return { trackOrderVisible: v } });
    }

    render() {
        const { loading, sideMenuOpen, lockScroll, paths, } = this.props; //initSignin
        const { announcement } = this.state;
        const notificationStyle = {
            Containers: {
                tr: {
                    top: '62px'
                }
            },
            NotificationItem: {
                info: {
                    color: '#fff', //'#f40e33',
                    backgroundColor: '#000000',
                    borderTop: '2px solid #cccccc'
                }
            },
            Dismiss: {
                info: {
                    color: '#fff',
                    backgroundColor: '#000'
                }
            }
        };
        const ALWAYS_ONLINE_PAGES = ['/privacy', '/tos'];

        if (this.props.status === STATUS.OFFLINE && !ALWAYS_ONLINE_PAGES.includes(this.props.location.pathname)) {
            return <Maintenance />
        }
        const st = { maxWidth: "100vw" }
        if (lockScroll) {
            st.height = this.state.vh;
        }
        const isProductPage = this.props.location.pathname.startsWith('/item') && !this.props.location.pathname.includes('addreview');
        return (
            <ScrollToTop>
                {loading && <Loading />}
                <div
                    id="main"
                    className={`main ${(sideMenuOpen || lockScroll) ? "side-menu-open" : ""}`}
                    style={st}
                    ref="main">
                    <Header showTrackOrder={this.showTrackOrder} authenticated={this.props.authenticated} announcement={announcement} />
                    <EmailVerificationPanel />
                    <EmailVerificationBanner />
                    <div id="bt-content" className={`bt-content ${Boolean(announcement) ? "with-announcement" : ""}`}>
                        {/* <Progress /> */}
                        <div style={{ minHeight: isProductPage ? 'calc(100vh)' : 'calc(100vh - 413.95px)' }}>
                            <Switch>
                                <Route exact path={`/`} component={Home} />
                                <Route path={`/login`} component={LoginView} />
                                <Route path={`/forgot_password`} component={ForgotView} />
                                <Route path={`/privacy`} component={PrivacyPolicy} />
                                <Route path={`/cookie-policy`} component={CookiePolicy} />
                                <Route path={`/tos`} component={TermOfService} />
                                <Route path={`/aboutus`} component={About} />
                                <Route path={`/contactus`} component={ContactUs} />
                                <Route path={`/partner`} component={Partner} />
                                <Route path={`/exchange_return`} component={ExchangeReturn} />
                                <Route path={`/store_locations/:store_id`} component={StoreLocation} />
                                <Route path={`/store_locations`} component={StoreLocation} />
                                {/* <Route path={`/reviews`} component={ReviewGrid} />
                                <Route path={`/review/:id`} component={ReviewPage} /> */}
                                {/* <Route path={`/best-sellers`} component={BestSeller} /> */}
                                <Route path={`/store/:param1?/:param2?/:param3?/:param4?`} render={(props) => <Redirect to={{ pathname: props.match.url.replace("/store", "/shop"), search: (props.location.search ? props.location.search : "") }} />} />
                                <Route path={`/shop/:param1?/:param2?/:param3?/:param4?`} component={StoreGrid} />
                                <Route path={`/brands`} component={BrandList} />
                                <Route exact path={`/deals/creditcard`} component={DealCreditCard} />
                                <Route path={`/deals`} component={Deal} />
                                <Route path={`/flashsale/:id?`} component={Flashsale} />
                                <Route exact path={`/item/:mat_id/:mat_var_id/addreview`} component={AddReview} />
                                <Route path={`/item/:mat_id/:mat_var_id/selectshade`} component={SelectShade} />
                                <Route path={`/item/:mat_id/:mat_var_id`} component={ProductDetail} />
                                <Route path={`/item/:mat_id`} component={ProductDetail} />
                                <Route exact path={`/checkout/thankyou/:so_no`} component={CheckoutThankYou} />
                                <Route exact path={`/checkout/thankyou`} component={CheckoutThankYou} />
                                <Route exact path={`/checkout`} component={Checkout} />
                                <Route exact path={`/order/:id`} component={OrderDetailView} />
                                <Route path={`/account`} component={AccountLayout} />
                                <Route exact path={`/upload_receipt`} component={UploadReceipt} />
                                <Route exact path={`/upload_receipt/thank_you`} component={UploadReceiptThankYou} />
                                <Route path={`/search/:keyword`} component={SearchResult} />
                                <Route path={`/auth/action`} component={FirebaseAction} />
                                <Route path={`/blogs`} component={BlogGrid} />
                                <Route path={`/blog/:id`} component={BlogContent} />
                                <Route path={`/howto`} component={Howto} />

                                <Route path={`/order/token/:token`} component={OrderDetailView} />
                                {/* <Route path={`/korea`} component={KoreaMain} /> */}

                                <Route path={`/chat_order`} exact component={ChatAndShopOrder} />
                                <Route path={`/chat_order/thankyou`} component={ChatAndShopThankyou} />

                                <Route path={`/event/:event_code`} component={TopSpender} />

                                {/*Giveaway*/}
                                <Route path={`/campaign/giveaway/create`} component={GiveawayForm} />
                                <Route path={`/campaign/giveaway/:id`} component={GiveawayDetail} />
                                <Route path={`/campaign/giveaway`} component={GiveawayMain} />
                                {/*End Region Giveaway*/}

                                {map(paths, (path, n) => {
                                    if (path.link.startsWith('/special-event')) {
                                        return <Route key={`page_${path.name}_${n}`} path={path.link} component={AppOnlyPage} />
                                    } else if (path.link !== "/") {
                                        const obj = {
                                            title: get(path, "seo_title", null),
                                            title_suffix: false,
                                            description: get(path, "seo_description", null),
                                            keywords: get(path, "seo_keywords", null),
                                            og_image: get(path, "og_image.url", null),
                                            imageType: get(path, "og_image.mime", null)
                                        }
                                        return <Route key={`page_${path.name}_${n}`} path={path.link} render={(props) => <PageRender page={path.name} {...props} {...obj} />} />
                                    }
                                    return null;
                                })}
                                <Route path={`*`} component={Page404} />
                            </Switch>
                        </div>
                        <Footer showTrackOrder={this.showTrackOrder} />
                    </div>
                    <ResponsiveMenu showTrackOrder={this.showTrackOrder} onAccount={this.props.location.pathname.startsWith('/account')} />
                    <CookieConsent />
                    <ReduxSweetAlert />
                    <NotificationSystem ref="notificationSystem" style={notificationStyle} />
                    <ItemAddedToCartNotify closePopup={() => this.setState({ addToCartVisible: false })} />
                    <AuthenticationPopup />
                    <RoyalPopup />
                    <PDPAConsentPopup />
                    {this.state.socket && <LiveChat socket={this.state.socket} />}
                    {this.state.trackOrderVisible && <TrackOrderPopup closeTrackOrder={() => this.setState({ trackOrderVisible: false })} />}
                </div>
            </ScrollToTop>
        );
    }
}

// function PrivateRoute ({component: Component, authed,
// showAuthenticationPopup, initSignin, ...rest}) {     if (!authed) {
// showAuthenticationPopup(true);     }     return <Route {...rest}
//        render={(props) => authed === true                     ? <Component
// {...props} />                     : <Redirect to={{pathname: '/', state:
// {from: props.location}}} />}             /> }

function mapStateToProps(state) {
    const profile = state.prof.profile;
    return {
        loading: state.common.loading,
        statusLoading: state.common.statusLoading, // Add statusLoading state
        authenticated: state.auth.authenticated,
        user_id: state.auth.user_id,
        email: profile ? profile.email : '',
        notification: state.common.notification,
        sideMenuOpen: state.common.show_responsive_menu,
        lockScroll: state.common.lock_scroll,
        initSignin: state.auth.initSignin,

        title: state.common.title,
        status: state.common.status,
        message: state.common.text,
        type: state.common.type,

        secret: state.auth.secret,
        couponUrl: state.coupon.couponUrl,

        paths: state.layout.paths,

        categories: state.itemCategories.categories,
    }
}

export default withRouter(connect(mapStateToProps, {
    clearOrder,
    showAuthenticationPopup,
    signinWithCookiePromise,
    queryStatus,
    swal,
    close,
    setMaintenance,
    postSocketError,
    reportClientError,
    // registerDevice,
    getCouponFromUrl,
    getCoupons,
    // displayCouponPopup,
    getAllCategories,
    getAllCategoriesPromise,
    getUserNotification,
    showRoyalPopup,
    setEID,
    getStaticPaths,
    getPageLayoutStrapi,
    accessTokenVerify,
    getRedirectFromProvider,
    getDeliveryRates,
    showPDPAPopup
})(frontloadConnect(frontLoad, {
    onMount: true,
    onUpdate: false
})(MainLayout)));
